// Edit-time client for the GraphQL selection-set tree endpoints (GraphQLSchemaController).
// All calls are scoped by connector + revision so a re-uploaded revision never serves a stale
// schema. Serialization back to a GraphQL string is done client-side (see selectionModel.ts); the
// only backend conversion is parse.

import { apiCall } from "@/core/api/api";

export interface FieldMeta {
  name: string;
  type: string;
  kind: "SCALAR" | "ENUM" | "OBJECT" | "INTERFACE" | "UNION";
  list: boolean;
  expandable: boolean;
  hasArgs: boolean;
  requiredArgs: boolean;
  deprecated: boolean;
  description?: string;
  members?: string[];
}

export interface TypeFields {
  typeName: string;
  kind: "OBJECT" | "INTERFACE" | "UNION";
  fields: FieldMeta[];
  members: string[];
}

export interface ParseResult {
  ok: boolean;
  data?: any;
}

function base(connectorId: string, revision: number | string): string {
  // apiCall's baseURL already includes the /rest/v1 prefix (mirrors flow.service's /connectorui).
  return `/connectors/${encodeURIComponent(connectorId)}/${revision}/graphql`;
}

export async function listTypeFields(
  connectorId: string,
  revision: number | string,
  typeName: string
): Promise<TypeFields | null> {
  const response = await apiCall(
    "GET",
    `${base(connectorId, revision)}/types/${encodeURIComponent(
      typeName
    )}/fields`
  );
  return response.code === 200 ? (response.result as TypeFields) : null;
}

export async function fetchDefaultSelection(
  connectorId: string,
  revision: number | string,
  operation: string,
  field: string
): Promise<any | null> {
  const response = await apiCall(
    "GET",
    `${base(connectorId, revision)}/actions/${encodeURIComponent(
      operation
    )}/${encodeURIComponent(field)}/default-selection`
  );
  return response.code === 200 ? response.result : null;
}

/**
 * Parses a saved selection string. A 422 means the selection is not representable as a tree
 * (arguments, aliases, fragments, drift) and the editor must fall back to raw text.
 */
export async function parseSelection(
  connectorId: string,
  revision: number | string,
  selectionSet: string,
  rootType: string
): Promise<ParseResult> {
  const response = await apiCall(
    "POST",
    `${base(connectorId, revision)}/selection/parse`,
    { selectionSet, rootType }
  );
  if (response.code === 200) {
    return { ok: true, data: response.result };
  }
  return { ok: false };
}
